import type { PaymentItem, PaymentMethod } from '@bssb/contracts';
import { apiRequest } from './api-client.ts';
import { db, readMeta, writeMeta } from './db.ts';

/**
 * Kuitansi lapangan (T-P7-24, FR-PAY-07/12).
 *
 * Nomor kuitansi resmi hanya boleh dibuat server — dua perangkat offline yang
 * membuat nomor sendiri pasti bertabrakan. Karena itu perangkat mencetak nomor
 * **sementara** yang ditandai jelas, lalu menukarnya dengan nomor final setelah
 * pembayarannya benar-benar tercatat. Nasabah tetap menerima bukti di tempat,
 * dan tidak ada nomor palsu yang beredar sebagai nomor resmi.
 */

const RECEIPT_PREFIX = 'receipt.';
const receiptKey = (clientUuid: string) => `${RECEIPT_PREFIX}${clientUuid}`;

export interface LocalReceipt {
  paymentClientUuid: string;
  visitClientUuid: string;
  loanId: string;
  loanNumber: string;
  customerName: string;
  amount: string;
  method: PaymentMethod;
  paidAt: string;
  /** Nomor sementara buatan perangkat; selalu berawalan `SMT-`. */
  temporaryNo: string;
  /** Nomor resmi dari server; `null` selama pembayaran belum tersinkron. */
  finalNo: string | null;
}

/**
 * Nomor sementara dibuat dari `clientUuid` pembayaran, bukan dari penghitung
 * lokal: penghitung akan mengulang dari nol setelah cache dihapus dan
 * menghasilkan dua kuitansi bernomor sama di perangkat yang sama.
 */
export function temporaryReceiptNo(clientUuid: string): string {
  return `SMT-${clientUuid.replaceAll('-', '').slice(-8).toUpperCase()}`;
}

export async function saveReceipt(receipt: LocalReceipt): Promise<void> {
  await writeMeta(receiptKey(receipt.paymentClientUuid), receipt);
}

export async function listReceipts(): Promise<LocalReceipt[]> {
  const rows = await db.meta.where('key').startsWith(RECEIPT_PREFIX).toArray();

  return (rows.map((row) => row.value as LocalReceipt).filter(Boolean) as LocalReceipt[]).sort(
    (a, b) => b.paidAt.localeCompare(a.paidAt),
  );
}

export async function getReceipt(clientUuid: string): Promise<LocalReceipt | null> {
  return (await readMeta<LocalReceipt>(receiptKey(clientUuid))) ?? null;
}

/**
 * Tukar nomor sementara dengan nomor final.
 *
 * Pencocokan lewat `clientUuid`, bukan lewat nominal dan waktu: dua setoran
 * dengan nominal sama di hari yang sama adalah hal biasa, dan menukar nomornya
 * berarti nasabah memegang bukti untuk pembayaran orang lain.
 */
export async function reconcileReceipts(): Promise<number> {
  const pending = (await listReceipts()).filter((receipt) => receipt.finalNo === null);
  if (pending.length === 0 || !navigator.onLine) return 0;

  // Item yang masih di outbox belum tentu sampai; menanyakannya ke server hanya
  // menghasilkan daftar kosong dan permintaan sia-sia di jaringan lapangan.
  const queued = new Set(await db.outbox.toCollection().primaryKeys());
  const settled = pending.filter((receipt) => !queued.has(receipt.visitClientUuid));
  if (settled.length === 0) return 0;

  let updated = 0;

  for (const loanId of new Set(settled.map((receipt) => receipt.loanId))) {
    const from = settled
      .filter((receipt) => receipt.loanId === loanId)
      .reduce((min, receipt) => (receipt.paidAt < min ? receipt.paidAt : min), '9999')
      .slice(0, 10);

    const response = await apiRequest<{ items: PaymentItem[] }>(
      `/payments?loanId=${loanId}&from=${from}&pageSize=50`,
    ).catch(() => null);

    if (!response) continue;

    const byClientUuid = new Map(response.items.map((item) => [item.clientUuid, item]));

    for (const receipt of settled.filter((entry) => entry.loanId === loanId)) {
      const match = byClientUuid.get(receipt.paymentClientUuid);
      if (!match?.receiptNo) continue;

      await saveReceipt({ ...receipt, finalNo: match.receiptNo });
      updated += 1;
    }
  }

  return updated;
}

/** Teks bagi-WhatsApp; sengaja polos agar terbaca di semua peramban WhatsApp. */
export function receiptShareText(receipt: LocalReceipt): string {
  const number = receipt.finalNo ?? `${receipt.temporaryNo} (sementara)`;

  return [
    'BUKTI SETORAN — BSSB',
    `No. kuitansi : ${number}`,
    `Nasabah      : ${receipt.customerName}`,
    `No. pinjaman : ${receipt.loanNumber}`,
    `Nominal      : Rp ${new Intl.NumberFormat('id-ID').format(Number(receipt.amount))}`,
    `Metode       : ${receipt.method}`,
    `Waktu        : ${new Date(receipt.paidAt).toLocaleString('id-ID')}`,
    '',
    receipt.finalNo === null
      ? 'Nomor masih sementara dan akan diganti nomor resmi setelah data terkirim ke sistem.'
      : 'Nomor kuitansi resmi.',
  ].join('\n');
}
